import React, { useState } from 'react';
import { apiService, ContactUsage } from '../../services/apiService';
import { trackClick } from '../../utils/analytics';
import { InlineAlert } from '../InlineAlert';

/**
 * The university-contact limit, as the user sees it (Backend/contact_limits.py).
 *
 * Opening a technology's university contacts counts once per technology per
 * window (the whole trial; a calendar month on a paid plan). Past the limit
 * the server answers with usage instead of addresses, and both Contact
 * University windows (ContactTTOModal and the legacy ContactUniversityModal)
 * render this in their place: how many were used, and a "Request more" form
 * that reaches the team (Admin → Users → Contact limits), who can raise this
 * account's limit. Technologies already opened keep opening normally — the
 * server decides that, not this.
 *
 * Copy says "university contacts", never quota or tier.
 */

export type ContactBlockReason = 'limit_reached' | 'upgrade_required';

/** Mirrors the backend's cap on `extra_requested`. */
const MAX_EXTRA = 100000;

/** "7 Oct 2026"; '' for anything unparseable rather than "Invalid Date". */
const sentOn = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? ''
        : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** "12 of 50 university contacts used during your trial" — shown under the
 *  header while the account is within a limit. Nothing when unlimited, or on
 *  the pre-limits fallback where there is no usage at all. */
export const ContactUsageLine: React.FC<{ usage?: ContactUsage | null; className?: string }> = ({
    usage, className = '',
}) => {
    if (!usage || usage.limit === null || usage.limit === undefined) return null;
    return (
        <p className={`text-grayscale-900 font-grotesk text-xs ${className}`}>
            {usage.used} of {usage.limit} university contacts used {usage.window_label}
        </p>
    );
};

interface ContactLimitNoticeProps {
    reason: ContactBlockReason;
    usage: ContactUsage | null;
    /** Shown read-only on the form; the server fills it in when absent. */
    userName?: string;
    /** For analytics only. */
    techId?: string;
    /** Only where the host can open the plans sheet; otherwise the
     *  upgrade case is just the message. */
    onUpgrade?: () => void;
}

export const ContactLimitNotice: React.FC<ContactLimitNoticeProps> = ({
    reason, usage, userName, techId, onUpgrade,
}) => {
    const [step, setStep] = useState<'idle' | 'form' | 'done'>('idle');
    const [extra, setExtra] = useState('');
    const [why, setWhy] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // An open request already on file: no second button, just its status.
    const [pending, setPending] = useState(usage?.pending_request || null);
    const name = (userName || '').trim();

    if (reason === 'upgrade_required') {
        return (
            <div className="rounded-xl border border-primary-300 bg-primary-100/40 px-4 py-3 flex flex-col gap-3">
                <p className="text-grayscale-1300 font-grotesk text-sm">
                    University contacts are part of our trial and paid plans.
                    {onUpgrade ? ' Start a trial or upgrade to see who to contact about this technology.' : ''}
                </p>
                {onUpgrade && (
                    <button type="button"
                        onClick={() => {
                            trackClick('contact_limit_click_upgrade', { tech_id: techId || null });
                            onUpgrade();
                        }}
                        className="self-start py-2 px-4 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium hover:bg-primary-600">
                        See plans
                    </button>
                )}
            </div>
        );
    }

    const used = usage?.used ?? null;
    const limit = usage?.limit ?? null;
    const when = usage?.window_label ? ` ${usage.window_label}` : '';

    const submit = async () => {
        const reasonText = why.trim();
        if (!reasonText) { setError("Tell us briefly what you're working on."); return; }
        let extraN: number | undefined;
        if (extra.trim()) {
            const n = Number(extra.trim());
            if (!Number.isInteger(n) || n < 1 || n > MAX_EXTRA) {
                setError('Enter a whole number for how many more, or leave it blank.');
                return;
            }
            extraN = n;
        }
        setBusy(true); setError('');
        trackClick('contact_limit_submit_request', {
            tech_id: techId || null, used, limit, extra_requested: extraN ?? null,
        });
        const res = await apiService.requestMoreContacts({
            reason: reasonText,
            ...(extraN !== undefined ? { extra_requested: extraN } : {}),
            ...(name ? { name } : {}),
        });
        setBusy(false);
        if (res.success && res.data?.success) {
            setPending(res.data.request);
            // A request was already open: nothing new went out, so show its
            // status rather than a fresh "thanks".
            setStep(res.data.duplicate ? 'idle' : 'done');
        } else {
            // A 400 carries a sentence meant for the user (e.g. empty reason);
            // anything else is ours to word.
            setError(res.status === 400 && res.error
                ? res.error : 'Could not send that just now. Please try again.');
        }
    };

    const sent = sentOn(pending?.created_at);

    return (
        <div className="rounded-xl border border-primary-300 bg-primary-100/40 px-4 py-3 flex flex-col gap-3">
            <div>
                <p className="text-grayscale-1300 font-grotesk text-sm font-medium">
                    {used !== null && limit !== null
                        ? `You've opened contacts for ${used} of ${limit} universities' technologies${when}.`
                        : `You've reached your limit for university contacts${when}.`}
                </p>
                <p className="text-grayscale-1100 font-grotesk text-xs mt-1">
                    Technologies you've already opened still show their contacts.
                </p>
            </div>

            {step === 'done' ? (
                <InlineAlert variant="success" role="status">
                    Thanks — we've received your request and will get back to you shortly.
                </InlineAlert>
            ) : pending ? (
                <p className="text-grayscale-1200 font-grotesk text-sm">
                    Your request for more contacts is with our team{sent ? ` (sent ${sent})` : ''}.
                </p>
            ) : step === 'idle' ? (
                <button type="button"
                    onClick={() => {
                        trackClick('contact_limit_click_request_more', { tech_id: techId || null, used, limit });
                        setStep('form'); setError('');
                    }}
                    className="self-start py-2 px-4 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium hover:bg-primary-600">
                    Request more
                </button>
            ) : (
                <div className="flex flex-col gap-3">
                    {name && (
                        <label className="flex flex-col gap-1">
                            <span className="text-grayscale-1100 font-grotesk text-xs">Your name</span>
                            <input value={name} readOnly
                                className="h-10 px-2.5 rounded-md bg-grayscale-200 text-grayscale-1100 text-sm font-grotesk focus:outline-none" />
                        </label>
                    )}
                    <label className="flex flex-col gap-1">
                        <span className="text-grayscale-1100 font-grotesk text-xs">
                            How many more do you need? (optional)
                        </span>
                        <input value={extra} onChange={e => setExtra(e.target.value)} disabled={busy}
                            type="number" inputMode="numeric" min={1} max={MAX_EXTRA} step={1}
                            placeholder="e.g. 25"
                            className="h-10 px-2.5 rounded-md bg-grayscale-100 border border-primary-200 text-sm font-grotesk focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-grayscale-1100 font-grotesk text-xs">What are you working on?</span>
                        <textarea value={why} onChange={e => setWhy(e.target.value)} rows={3}
                            maxLength={2000} disabled={busy} required
                            placeholder="A line or two about your project"
                            className="p-2.5 rounded-md bg-grayscale-100 border border-primary-200 text-sm font-grotesk resize-none focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    </label>

                    {error && <InlineAlert variant="error" compact>{error}</InlineAlert>}

                    <div className="flex items-center gap-3 justify-end">
                        <button type="button" onClick={() => { setStep('idle'); setError(''); }} disabled={busy}
                            className="py-2 px-3 text-grayscale-1200 font-grotesk text-sm">
                            Cancel
                        </button>
                        <button type="button" onClick={submit} disabled={busy}
                            className="py-2 px-4 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
                            {busy ? 'Sending…' : 'Send request'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactLimitNotice;
